import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EditBlogForm from './EditBlogForm';
import { useCreateBlogMutation } from '../blogsApiSlice';

function ConfirmDialog({ open, title = 'Discard changes?', message, confirmText = 'Discard', cancelText = 'Keep editing', onConfirm, onCancel, loading = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {message && <p className="mt-2 text-gray-600">{message}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="rounded-xl border px-4 py-2 text-gray-700 hover:bg-gray-50" disabled={loading}>
            {cancelText}
          </button>
          <button onClick={onConfirm} className="rounded-xl bg-red-600 px-4 py-2 text-white hover:opacity-90 disabled:opacity-60" disabled={loading}>
            {loading ? 'Discarding…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NewBlog() {
  const navigate = useNavigate();
  const [createBlog, { isLoading }] = useCreateBlogMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);

  const onSubmit = async (payload, { markClean }) => {
    try {
      await createBlog(payload).unwrap();
      markClean?.();
      navigate('/admin/blogs');
    } catch (e) {
      console.error(e);
      alert(e?.data?.message ?? 'Failed to create blog');
    }
  };

  const onCancel = ({ dirty }) => {
    if (dirty) {
      setPendingCancel(true);
      setConfirmOpen(true);
    } else {
      navigate('/admin/blogs');
    }
  };

  const confirmDiscard = () => {
    setConfirmOpen(false);
    if (pendingCancel) navigate('/admin/blogs');
  };

  const handleSaved = (saved) => {
    // after create, navigate back to list or to edit new item
    if (saved?.id) {
      navigate('/dash/blogs');
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Create Blog</h1>
          <p className="text-gray-600">Create a new blog post</p>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/dash/blogs" className="rounded-xl border px-4 py-2 text-gray-700 hover:bg-gray-50">
            Back to list
          </Link>
        </div>
      </div>

      <EditBlogForm 
        onSaved={handleSaved}
        onCancel={() => navigate(-1)}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        onConfirm={confirmDiscard}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
